import React, { useState } from 'react';

export default function AdminCustomerSupportSection({ appId }) {
  const [expandedSections, setExpandedSections] = useState({
    plain: false
  });
  const [showIntegration, setShowIntegration] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>💬</span>
          <span>Customer Support</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Customer support tools and live chat configuration
        </p>
      </div>

      <div className="space-y-4">
        {/* Plain */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('plain')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">Plain</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.plain ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.plain && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <div className="space-y-2 text-sm text-gray-700">
                <p className="text-xs text-gray-600 mb-3">
                  Plain is a customer support platform with a modern API-first approach. Add a chat widget to your app so users can reach your support team directly.
                </p>
                <div className="text-xs text-gray-600 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <strong className="text-amber-800">Clearing customer data on logout:</strong>
                  {' '}Call <code className="bg-amber-100 px-1 rounded font-mono">Plain.setCustomerDetails(null)</code>. The function accepts <code className="bg-amber-100 px-1 rounded font-mono">null</code> and explicitly handles logout in the session initialization logic.
                </div>
                <div className="mt-3">
                  <a
                    href="https://app.plain.com/workspace/w_01KGZ4FGX2HSNERD4DSFQMB7BA/settings/chat/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-zapt-purple rounded-lg hover:bg-zapt-purple/90 transition-colors"
                  >
                    Add Chat to App →
                  </a>
                </div>

                {/* Integration Instructions */}
                <div className="mt-6 pt-4 border-t border-gray-300">
                  <button
                    onClick={() => setShowIntegration(!showIntegration)}
                    className="flex items-center gap-2 text-sm font-semibold text-gray-900"
                  >
                    <span className="text-gray-500 text-xs">
                      {showIntegration ? '▼' : '▶'}
                    </span>
                    Integration Instructions
                  </button>

                  {showIntegration && (
                    <div className="mt-4 space-y-6">
                      <p className="text-xs text-gray-600">
                        Copy these three pieces into each app. The only thing that changes between apps is the <code className="bg-gray-200 px-1 rounded">authenticate()</code> function in the API endpoint — adapt it to whatever auth system each app uses. Everything else is copy-paste.
                      </p>

                      {/* 1. React Component */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">1. React Component — <code className="bg-gray-200 px-1 rounded text-xs">PlainChat.jsx</code></h5>
                        <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`import { useEffect, useRef } from 'react';

const PLAIN_APP_ID = import.meta.env.VITE_PLAIN_CHAT_APP_ID;
const SCRIPT_SRC = 'https://chat.cdn-plain.com/index.js';

export default function PlainChat({ getCustomerDetails }) {
  const initialised = useRef(false);

  useEffect(() => {
    if (!PLAIN_APP_ID || initialised.current) return;
    if (document.querySelector(\`script[src="\${SCRIPT_SRC}"]\`)) return;

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = false;
    script.onload = () => {
      window.Plain.init({ appId: PLAIN_APP_ID, theme: 'auto' });
      initialised.current = true;
    };
    document.head.appendChild(script);
  }, []);

  // Identify authenticated users with HMAC-verified email
  useEffect(() => {
    if (!getCustomerDetails) return;

    async function identify() {
      if (!initialised.current || !window.Plain) return;
      try {
        const details = await getCustomerDetails();
        if (details) window.Plain.setCustomerDetails(details);
      } catch (err) {
        console.warn('PlainChat: failed to identify customer', err);
      }
    }

    identify();
  }, [getCustomerDetails]);

  return null;
}`}
                        </pre>
                      </div>

                      {/* 2. Server Endpoint */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">2. Server Endpoint — <code className="bg-gray-200 px-1 rounded text-xs">api/plain-hash.js</code></h5>
                        <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`import crypto from 'node:crypto';

// Adapt authenticate() to whatever auth your app uses
import { authenticate } from './_middleware.js';

export default async function handler(req, res) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const { member } = await authenticate(req);
    const secret = process.env.PLAIN_CHAT_SECRET;

    if (!secret) {
      return res.status(503).json({ error: 'Plain chat signing not configured' });
    }

    const emailHash = crypto
      .createHmac('sha256', secret)
      .update(member.email_address)
      .digest('hex');

    return res.status(200).json({ emailHash });
  } catch (error) {
    const status = error.status || 500;
    return res.status(status).json({ error: error.message || 'Internal server error' });
  }
}`}
                        </pre>
                      </div>

                      {/* 3. Usage in App Root */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">3. Usage in Your App Root</h5>
                        <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`// Wherever you mount your app (e.g. main.jsx)
import PlainChat from './components/PlainChat.jsx';

// Pass a function that fetches the hash and returns customer details
// Adapt to however your app gets the current user
function useCustomerDetails() {
  const user = useCurrentUser(); // your auth hook

  return user
    ? async () => {
        const res = await fetch('/api/plain-hash');
        if (!res.ok) return null;
        const { emailHash } = await res.json();
        return {
          email: user.email,
          emailHash,
          fullName: user.name,
          shortName: user.name?.split(' ')[0],
        };
      }
    : null;
}

// In your render tree
<PlainChat getCustomerDetails={useCustomerDetails()} />`}
                        </pre>
                      </div>

                      {/* Env Vars */}
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2">Env Vars (same for every app)</h5>
                        <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`VITE_PLAIN_CHAT_APP_ID=<from Plain Settings → Chat → App ID>
PLAIN_CHAT_SECRET=<from Plain Settings → Chat → Generate secret>`}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
